/**
 * The typed Client-Portal API client (T-7.10; US-100). ONE fetch surface for the
 * whole portal SPA, pointed at the READ-ONLY `/api/v1/portal/*` backend (portal.ts).
 * Every request is same-origin with `credentials: 'include'` so the HttpOnly
 * `argus_session` cookie rides along — the SPA never sees or stores the token
 * (auth-and-authz.md §1.3). Every failure becomes a typed `ApiError` carrying
 * `status` + machine `code` + a non-revealing `message` (never a stack).
 *
 * The client mounts the READ surface plus the ONE feature-gated mutation the server
 * offers — `uploadDocument` (T-7.11), a client uploading into their OWN case when the
 * firm has enabled portal uploads; every other method is a GET read (CC-2). The types
 * below are the client-safe projections portal.ts serialises (CC-4) — no staff-only fields.
 */
export const PORTAL_BASE = '/api/v1/portal';

/** A client-safe case projection (CC-4). */
export interface PortalCase {
  case_number: string;
  title: string;
  engagement_type: string;
  status: string;
  period_start: string;
  period_end: string;
  created_at: string;
  updated_at: string;
  /**
   * Whether the firm has enabled client-portal uploads (the per-firm
   * `portal_uploads_enabled` flag; T-7.11). Present on the single-case view
   * (getCase) so the SPA shows the upload control only when the firm opted in;
   * omitted from the case LIST (listCases).
   */
  uploads_enabled?: boolean;
}

/** A client-safe document projection (CC-4 — no extracted_fields / r2 keys / sha256). */
export interface PortalDocument {
  id: string;
  filename: string;
  doc_type: string | null;
  size_bytes: number;
  page_count: number | null;
  uploaded_at: string;
}

/** A downloadable artefact format for a shared report. */
export type ReportFormat = 'html' | 'pdf' | 'docx';

/** A client-safe report projection (CC-4 — no r2 keys / sha256 / generator identity). */
export interface PortalReport {
  id: string;
  report_type: string;
  title: string;
  version: number;
  status: string;
  is_court_bundle: boolean;
  generated_at: string;
  available_formats: ReportFormat[];
}

/** The client's own profile (CC-4 — never the password hash / internal columns). */
export interface PortalProfile {
  id: string;
  email: string;
  full_name: string;
  role: string;
}

/** A derived notification item (no durable substrate in V1). */
export interface PortalNotification {
  type: 'report_available' | 'case_closed' | 'case_archived';
  case_number: string;
  title: string;
  at: string;
}

/** The NDPA Part V right a DSAR exercises (T-7.14; US-153). */
export type DsarRequestType = 'access' | 'rectification' | 'erasure';

/** A client-safe DSAR-request projection (CC-4 — the subject's OWN request only). */
export interface PortalDsarRequest {
  id: string;
  request_type: DsarRequestType;
  status: 'submitted' | 'in_progress' | 'fulfilled' | 'refused';
  case_number: string;
  requested_at: string;
  resolved_at?: string;
  refusal_reason?: string;
}

/** The shape the backend `Error` schema surfaces. */
export interface ApiErrorBody {
  code: string;
  message: string;
  /** F-37: worker-api's per-request correlation id (also the `x-request-id` header). */
  request_id?: string;
}

/** A typed, non-revealing API failure (CC-7). Consumers narrow on `.status`. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /**
   * F-37: the request's correlation id — the `x-request-id` response header (set on EVERY
   * worker-api response; Cloudflare's `cf-ray` when present), else the body's `request_id`.
   * Show it with an error so support can find the request in the logs. Absent on
   * transport failures.
   */
  readonly requestId?: string;

  constructor(status: number, body: ApiErrorBody, requestId?: string) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    const rid = requestId ?? body.request_id;
    if (rid) this.requestId = rid;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/** Build the typed failure for a non-OK response: parsed body + the `x-request-id` header. */
async function errorFrom(res: Response): Promise<ApiError> {
  let body: ApiErrorBody = { code: 'error', message: 'Request failed.' };
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    /* non-JSON error body — keep the default */
  }
  return new ApiError(res.status, body, res.headers.get('x-request-id') ?? undefined);
}

/** Narrowing guard. */
export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/** A safe, human message for a caught error (never a stack). */
export function getErrorMessage(err: unknown): string {
  if (isApiError(err)) {
    if (err.status === 401) return 'Your session has expired. Please sign in again.';
    return err.message;
  }
  return 'Something went wrong. Please try again.';
}

async function get<T>(path: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${PORTAL_BASE}${path}`, {
      method: 'GET',
      credentials: 'include',
      headers: { accept: 'application/json' },
    });
  } catch {
    // A transport failure (offline / DNS) — never leak the underlying error.
    throw new ApiError(0, { code: 'network_error', message: 'Network error. Please try again.' });
  }
  if (!res.ok) throw await errorFrom(res);
  return (await res.json()) as T;
}

/**
 * POST a multipart body (T-7.11 upload). Same-origin with `credentials: 'include'` so the
 * HttpOnly session cookie rides along; the browser sets the multipart Content-Type +
 * boundary (never set it by hand). Failures become the SAME typed, non-revealing ApiError
 * the read path uses (CC-7).
 */
async function send<T>(path: string, form: FormData): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${PORTAL_BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { accept: 'application/json' },
      body: form,
    });
  } catch {
    throw new ApiError(0, { code: 'network_error', message: 'Network error. Please try again.' });
  }
  if (!res.ok) throw await errorFrom(res);
  return (await res.json()) as T;
}

/**
 * POST a JSON body (T-7.14 DSAR request submission). Same-origin with the session cookie;
 * failures become the SAME typed, non-revealing ApiError (CC-7).
 */
async function postJson<T>(path: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${PORTAL_BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, { code: 'network_error', message: 'Network error. Please try again.' });
  }
  if (!res.ok) throw await errorFrom(res);
  return (await res.json()) as T;
}

/** The whole portal surface, one function per backend route (reads + the T-7.11 upload). */
export const portalApi = {
  async listCases(): Promise<PortalCase[]> {
    const { cases } = await get<{ cases: PortalCase[] }>('/cases');
    return cases;
  },
  getCase(caseNumber: string): Promise<PortalCase> {
    return get<PortalCase>(`/cases/${encodeURIComponent(caseNumber)}`);
  },
  async listDocuments(caseNumber: string): Promise<PortalDocument[]> {
    const { documents } = await get<{ documents: PortalDocument[] }>(
      `/cases/${encodeURIComponent(caseNumber)}/documents`,
    );
    return documents;
  },
  async listReports(caseNumber: string): Promise<PortalReport[]> {
    const { reports } = await get<{ reports: PortalReport[] }>(
      `/cases/${encodeURIComponent(caseNumber)}/reports`,
    );
    return reports;
  },
  getProfile(): Promise<PortalProfile> {
    return get<PortalProfile>('/profile');
  },
  async listNotifications(): Promise<PortalNotification[]> {
    const { notifications } = await get<{ notifications: PortalNotification[] }>('/notifications');
    return notifications;
  },
  /**
   * Upload a document into the client's OWN case (T-7.11; US-101) — a multipart POST
   * carrying the single `file` part, same-origin with the session cookie. The server
   * computes the SHA-256, stores the immutable original, and enqueues OCR. Returns the
   * client-safe `PortalDocument`. A 403 means the firm has not enabled portal uploads;
   * a 404 means the case is not the caller's own (both surface as a typed ApiError).
   */
  uploadDocument(caseNumber: string, file: File): Promise<PortalDocument> {
    const form = new FormData();
    form.append('file', file);
    return send<PortalDocument>(`/cases/${encodeURIComponent(caseNumber)}/documents`, form);
  },
  /**
   * The client's OWN submitted DSAR requests (T-7.14; US-153), newest-first — the
   * subject sees only its own (CC-1/CC-4). A read: no side effects.
   */
  async listDsarRequests(): Promise<PortalDsarRequest[]> {
    const { requests } = await get<{ requests: PortalDsarRequest[] }>('/dsar-requests');
    return requests;
  },
  /**
   * Submit a data-subject request (access | rectification | erasure) for the client's OWN
   * data in a case it is a member of (CC-1). The client can ONLY request — a firm admin
   * fulfils it. A 404 means the case is not the caller's own (a typed, non-revealing
   * ApiError); a 400 means an invalid request_type / body.
   */
  submitDsarRequest(
    caseNumber: string,
    requestType: DsarRequestType,
    details?: string,
  ): Promise<PortalDsarRequest> {
    return postJson<PortalDsarRequest>('/dsar-requests', {
      case_number: caseNumber,
      request_type: requestType,
      ...(details ? { details } : {}),
    });
  },
};

/**
 * The href for a report artefact download. Points at the portal download route, which
 * 302-redirects to a time-limited signed R2 URL (portal.ts → mintReportDownloadUrl).
 * Used as a plain `<a href>` so the browser follows the redirect and streams the file;
 * the SPA never handles the token or the artefact bytes.
 */
export function reportDownloadHref(
  caseNumber: string,
  reportId: string,
  format: ReportFormat,
): string {
  return (
    `${PORTAL_BASE}/cases/${encodeURIComponent(caseNumber)}` +
    `/reports/${encodeURIComponent(reportId)}/download?format=${encodeURIComponent(format)}`
  );
}

/**
 * The href for a shared document download (F-35). Points at the portal document download
 * route, which 302-redirects to a time-limited worker-signed URL (portal.ts →
 * mintUploadUrl — the SAME tokened target the staff download uses). Used as a plain
 * `<a href>`: the browser follows the redirect and streams the original; the SPA never
 * handles the token or the bytes. A document that is not shared with the client (not of
 * their own case, deleted, or not yet finalized) answers 404 at the redirect.
 */
export function documentDownloadHref(caseNumber: string, documentId: string): string {
  return (
    `${PORTAL_BASE}/cases/${encodeURIComponent(caseNumber)}` +
    `/documents/${encodeURIComponent(documentId)}/download`
  );
}
